import { FC, useCallback, useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { SCUILoadingLinear, SCUIPagination } from '../../../../atomic/atoms';
import { SCUITextFieldWithButton } from '../../../../atomic/molecules';
import { NoResultFoundComponent } from '../NoResultFoundComponent/NoResultFoundComponent';
import { fetchStoreOrderSummaryList } from './customerStoreOrders.fetch';
import type { StoreOrderListSummary, StoreOrderTimeFilterOption } from './customerStoreOrders.types';
import { STORE_ORDER_LIST_TYPE } from './customerStoreOrders.types';
import { CustomerStoreOrderRow } from './CustomerStoreOrderRow';
import { CSOP } from './customerStoreOrdersPaper.classes';
import type { StoreReceiptDisplayMode } from './customerStoreOrders.helpers';

interface CustomerStoreOrdersPaperProps {
    datas: { customer: { customerId: number } };
    receiptDisplayMode?: StoreReceiptDisplayMode;
}

export const CustomerStoreOrdersPaper: FC<CustomerStoreOrdersPaperProps> = ({
    datas,
    receiptDisplayMode = 'download',
}) => {
    const { t } = useTranslation();
    const isDarkMode = useSelector((state: { darkMode: { isDarkMode: boolean } }) => state.darkMode.isDarkMode);

    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [recordCount, setRecordCount] = useState(0);
    const [orders, setOrders] = useState<StoreOrderListSummary[]>([]);
    const [orderNumberSearch, setOrderNumberSearch] = useState('');
    const [appliedSearchFilter, setAppliedSearchFilter] = useState<string | null>(null);
    const [timeFilterList, setTimeFilterList] = useState<StoreOrderTimeFilterOption[]>([]);
    const [selectedTimeFilter, setSelectedTimeFilter] = useState<StoreOrderTimeFilterOption | null>(null);

    const customerId = datas?.customer?.customerId;

    const loadOrders = useCallback(async () => {
        if (!customerId) {
            return;
        }
        setPageLoading(true);
        setLoading(true);
        try {
            const result = await fetchStoreOrderSummaryList({
                customerId,
                skip: page * rowsPerPage,
                take: rowsPerPage,
                orderListType: STORE_ORDER_LIST_TYPE,
                timeFilter: selectedTimeFilter?.key ?? null,
                searchFilter: appliedSearchFilter,
            });
            setOrders(result.data);
            setRecordCount(result.count);
            if (result.timeFilterList.length > 0) {
                setTimeFilterList(result.timeFilterList);
            }
        } catch {
            Swal.fire(t('general.error'), ' ', 'error');
            setOrders([]);
            setRecordCount(0);
        } finally {
            setLoading(false);
            setPageLoading(false);
        }
    }, [appliedSearchFilter, customerId, page, rowsPerPage, selectedTimeFilter, t]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const handleChangeRows = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = () => {
        setPage(0);
        const trimmed = orderNumberSearch.trim();
        setAppliedSearchFilter(trimmed || null);
    };

    return (
        <Grid className={CSOP.page}>
            <Box className={CSOP.header} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StorefrontIcon color="primary" />
                <Typography variant="h6">{t('customerStoreOrders.title')}</Typography>
            </Box>
            <Grid
                className={CSOP.searchRow}
                container
                spacing={2}
                sx={{ marginBottom: '1rem', alignItems: 'center' }}
            >
                <Grid size={{ xl: 6, lg: 6, md: 12, xs: 12 }}>
                    <Box component="form" sx={{ width: '100%' }} onSubmit={(e) => {
                            e.preventDefault();
                            handleSearch();
                        }}>
                        <SCUITextFieldWithButton
                            width="100%"
                            inputClassName={CSOP.inputOrderNumberSearch}
                            placeholder={t('general.orderNo')}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                setOrderNumberSearch(event.target.value);
                            }}
                            onClick={handleSearch}
                            onKeyPress={(e: KeyboardEvent) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                    </Box>
                </Grid>
                {timeFilterList.length > 0 && (
                    <Grid size={{ xl: 6, lg: 6, md: 12, xs: 12 }}>
                        <FormControl fullWidth size="small" sx={{ maxWidth: { xl: 450, lg: 450 } }}>
                            <InputLabel id="store-order-time-filter-label">
                                {t('customerStoreOrders.timeFilter')}
                            </InputLabel>
                            <Select
                                labelId="store-order-time-filter-label"
                                label={t('customerStoreOrders.timeFilter')}
                                value={selectedTimeFilter?.key ?? ''}
                                onChange={(e) => {
                                    setPage(0);
                                    const key = e.target.value;
                                    if (!key) {
                                        setSelectedTimeFilter(null);
                                        return;
                                    }
                                    const match = timeFilterList.find((item) => item.key === key);
                                    setSelectedTimeFilter(match ?? { key, value: key });
                                }}
                            >
                                <MenuItem value="">
                                    <em>{t('general.all')}</em>
                                </MenuItem>
                                {timeFilterList.map((item) => (
                                    <MenuItem key={item.key} value={item.key}>
                                        {item.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
            </Grid>
            <Paper className={CSOP.mainPaper} sx={{ width: '100%', p: 1 }}>
                <SCUILoadingLinear display={loading} />
                {orders.length > 0 ? (
                    <Grid className={CSOP.tableSection} size={{ xl: 12, lg: 12, md: 12, xs: 12 }}>
                        <TableContainer className={CSOP.tableContainer} component={Paper}>
                            <Table aria-label="store orders table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell align="center">{t('general.orderNo')}</TableCell>
                                        <TableCell align="center">{t('general.orderDate')}</TableCell>
                                        <TableCell align="left">{t('customerStoreOrders.storeName')}</TableCell>
                                        <TableCell align="center">{t('customerStoreOrders.grandTotal')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orders.map((row) => (
                                        <CustomerStoreOrderRow
                                            key={row.rowId}
                                            order={row}
                                            customerId={customerId}
                                            orderListType={STORE_ORDER_LIST_TYPE}
                                            isDarkMode={isDarkMode}
                                            receiptDisplayMode={receiptDisplayMode}
                                        />
                                    ))}
                                </TableBody>
                                <SCUIPagination
                                    rowsPerPageOptions={[10, 20, 50]}
                                    count={recordCount}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onRowsPerPageChange={handleChangeRows}
                                    setPage={setPage}
                                />
                            </Table>
                        </TableContainer>
                    </Grid>
                ) : !pageLoading ? (
                    <NoResultFoundComponent errorText={t('customerStoreOrders.noOrders')} />
                ) : (
                    <SCUILoadingLinear display={pageLoading} />
                )}
            </Paper>
        </Grid>
    );
};
